import { useCallback, useState } from 'react';
import { KeenIcon } from '@/components';
import { Modal } from '@/components/modal/Modal';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { formatDisplayDateTime } from '@/utils/Date';
import {
  useGetPendingThemeRouteApprovalsQuery,
  useReviewThemeRouteApprovalMutation,
  useAddThemeRouteCommentMutation,
} from '@/services/ThemeRoutesService';

interface ThemeRouteApprovalItem {
  id: string;
  theme_route_id: string;
  requested_by: string;
  requester?: {
    id: string;
    name?: string;
    email?: string;
  };
  reviewed_by?: string | null;
  status: number;
  notes?: string | null;
  rejection_reason?: string | null;
  reviewed_at?: string | null;
  created_at?: string;
  updated_at?: string;
  themeRoute?: {
    name?: string;
  };
}

const ThemeRouteApprovalsTable = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: pendingApprovalsResponse,
    isLoading: isApprovalsLoading,
    refetch: refetchApprovals,
  } = useGetPendingThemeRouteApprovalsQuery();

  const [reviewApproval, { isLoading: isReviewingApproval }] =
    useReviewThemeRouteApprovalMutation();
  const [addComment] = useAddThemeRouteCommentMutation();

  const [approvalModalState, setApprovalModalState] = useState<{
    isOpen: boolean;
    mode: 'approve' | 'reject' | null;
    approvalId: string | null;
    themeRouteId: string | null;
  }>({ isOpen: false, mode: null, approvalId: null, themeRouteId: null });

  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const openApprovalModal = useCallback(
    (approvalId: string, mode: 'approve' | 'reject', themeRouteId: string) => {
      setApprovalModalState({
        isOpen: true,
        mode,
        approvalId,
        themeRouteId,
      });
      setApprovalNotes('');
      setRejectionReason('');
    },
    []
  );

  const closeApprovalModal = useCallback(() => {
    setApprovalModalState({
      isOpen: false,
      mode: null,
      approvalId: null,
      themeRouteId: null,
    });
    setApprovalNotes('');
    setRejectionReason('');
  }, []);

  const handleSubmitApprovalReview = useCallback(async () => {
    if (!approvalModalState.approvalId || !approvalModalState.mode) return;

    const isApprove = approvalModalState.mode === 'approve';
    const statusValue = isApprove ? 1 : 2;

    const themeRouteId = approvalModalState.themeRouteId;
    const commentText = (approvalNotes || rejectionReason).trim();

    try {
      await reviewApproval({
        approvalId: approvalModalState.approvalId,
        status: statusValue,
        notes: approvalNotes || undefined,
        rejection_reason: !isApprove ? rejectionReason || undefined : undefined,
      }).unwrap();

      // Optionally add a comment on the theme route with the admin's notes
      if (themeRouteId && commentText) {
        try {
          await addComment({
            id: themeRouteId,
            comment: commentText,
          }).unwrap();
        } catch {
          enqueueSnackbar(
            'Approval decision saved, but failed to add comment to theme route',
            {
              variant: 'warning',
            }
          );
        }
      }

      enqueueSnackbar(
        isApprove ? 'Theme route approved' : 'Theme route rejected',
        { variant: 'success' }
      );
      closeApprovalModal();
      refetchApprovals();
    } catch {
      enqueueSnackbar('Failed to review approval request', {
        variant: 'error',
      });
    }
  }, [
    approvalModalState.approvalId,
    approvalModalState.mode,
    approvalModalState.themeRouteId,
    approvalNotes,
    rejectionReason,
    reviewApproval,
    addComment,
    enqueueSnackbar,
    closeApprovalModal,
    refetchApprovals,
  ]);

  return (
    <>
      <div className="card card-grid min-w-full">
        <div className="card-header flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-800">
            Pending approval requests
          </h2>
        </div>
        <div className="card-body">
          {isApprovalsLoading ? (
            <div className="py-4 text-sm text-gray-500">
              Loading approval requests...
            </div>
          ) : !pendingApprovalsResponse ||
            !Array.isArray(pendingApprovalsResponse.data) ||
            pendingApprovalsResponse.data.length === 0 ? (
            <div className="py-4 text-sm text-gray-500">
              No pending approval requests.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-sm w-full">
                <thead>
                  <tr>
                    <th className="text-left">Route</th>
                    <th className="text-left">Requested By</th>
                    <th className="text-left">Requested At</th>
                    <th className="text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(
                    pendingApprovalsResponse.data as ThemeRouteApprovalItem[]
                  ).map((item) => {
                    const approvalId = String(item.id);
                    const routeName =
                      item.themeRoute?.name ||
                      item.theme_route_id ||
                      '(Unknown route)';
                    const requestedBy =
                      item.requester?.name ||
                      item.requester?.email ||
                      item.requested_by ||
                      '-';
                    const createdAtRaw = item.created_at;
                    const createdAt = createdAtRaw
                      ? formatDisplayDateTime(new Date(createdAtRaw))
                      : '-';

                    return (
                      <tr key={approvalId || `${routeName}-${createdAt}`}>
                        <td className="text-sm text-gray-800">{routeName}</td>
                        <td className="text-sm text-gray-600">{requestedBy}</td>
                        <td className="text-sm text-gray-600">{createdAt}</td>
                        <td className="text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="btn btn-xs btn-light flex items-center gap-1"
                              onClick={() =>
                                navigate(
                                  `/theme-routes/view/${item.theme_route_id}`,
                                  {
                                    state: { from: 'approvals' },
                                  }
                                )
                              }
                            >
                              <KeenIcon
                                type="filled"
                                icon="eye"
                                className="text-gray-600"
                              />
                              <span>View</span>
                            </button>
                            <button
                              type="button"
                              className="btn btn-xs btn-success flex items-center gap-1 contrast:bg-green-700 contrast:hover:bg-green-800"
                              onClick={() =>
                                openApprovalModal(
                                  approvalId,
                                  'approve',
                                  item.theme_route_id
                                )
                              }
                              disabled={isReviewingApproval}
                            >
                              <KeenIcon
                                type="filled"
                                icon="check"
                                className="text-white contrast:text-white"
                              />
                              <span className="contrast:text-white">
                                Approve
                              </span>
                            </button>
                            <button
                              type="button"
                              className="btn btn-xs btn-light border border-gray-300 text-red-600 flex items-center gap-1"
                              onClick={() =>
                                openApprovalModal(
                                  approvalId,
                                  'reject',
                                  item.theme_route_id
                                )
                              }
                              disabled={isReviewingApproval}
                            >
                              <KeenIcon
                                type="filled"
                                icon="cross"
                                className="text-red-600"
                              />
                              <span>Reject</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal
        open={approvalModalState.isOpen}
        onClose={closeApprovalModal}
        className="!flex items-center justify-center"
        zIndex={120}
        aria-labelledby="theme-route-approval-dialog-title"
      >
        <div className="card w-full max-w-md mx-4">
          <div className="card-header flex items-center justify-between">
            <h3
              id="theme-route-approval-dialog-title"
              className="text-sm font-semibold text-gray-800"
            >
              {approvalModalState.mode === 'approve'
                ? 'Approve theme route request'
                : 'Reject theme route request'}
            </h3>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label
                className="form-label text-xs font-medium text-gray-700"
                htmlFor="approval-notes"
              >
                Comments (optional)
              </label>
              <textarea
                id="approval-notes"
                className="form-control min-h-[80px] text-sm leading-5 w-full p-3 contrast:bg-black"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Add an optional comment to explain your decision or give guidance for future changes."
              />
            </div>
            {approvalModalState.mode === 'reject' && (
              <div>
                <label
                  className="form-label text-xs font-medium text-gray-700"
                  htmlFor="rejection-reason"
                >
                  Rejection reason
                </label>
                <textarea
                  id="rejection-reason"
                  className="form-control min-h-[80px] text-sm leading-5 w-full p-3 contrast:bg-black"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Provide a clear reason for rejecting this request so the route author knows what to improve."
                />
              </div>
            )}
          </div>
          <div className="card-footer flex justify-end gap-2">
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={closeApprovalModal}
              disabled={isReviewingApproval}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleSubmitApprovalReview}
              disabled={isReviewingApproval}
            >
              {approvalModalState.mode === 'approve'
                ? 'Confirm approval'
                : 'Confirm rejection'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ThemeRouteApprovalsTable;
